import { Tab } from "@dmm-com/rocket-web-ui-library";
import { memo } from "react";
import type { TabType } from "~/components/blocks/product/const";
import { MULTI_SELECT_TABS } from "~/components/blocks/product/const";
import { G_VideoSeasonType } from "~/lib/graphql";
import { useAuth } from "~/provider/auth";

export type VideoSeasonTabTitleProps = {
  /** 現在選択中のタブの値 */
  selectedTabValue: string;
  /** タブの切り替え処理 */
  onChangeTabValue: (tab: TabType) => void;
  /** シーズンタイプ */
  seasonType: G_VideoSeasonType;
  /** エピソードの合計数 */
  totalEpisodeLength: number;
  /** 購入済みの合計数 */
  totalPurchasedLength: number;
  /** 特典映像の合計数 */
  totalSpecialLength: number;
  /** 予告編映像の合計数 */
  totalPreviewLength: number;
  /** 視聴制限 */
  isAdultBlock: boolean;
};

/**
 * 複数話 | 作品末端のTabTitle部分に関するコンポーネント
 * @param props {@link VideoSeasonTabTitleProps}
 */
export const VideoSeasonTabTitle = memo((props: VideoSeasonTabTitleProps) => {
  const {
    selectedTabValue,
    onChangeTabValue,
    seasonType,
    totalEpisodeLength,
    totalPurchasedLength,
    totalSpecialLength,
    totalPreviewLength,
    isAdultBlock,
  } = props;

  const { isLoggedIn } = useAuth();

  /** エピソードが1話以上だった場合、エピソードタブを表示すること */
  const isEpisode = isAdultBlock && totalEpisodeLength > 0 && seasonType === G_VideoSeasonType.MultiEpisodes;
  /** エピソードが1話以上かつ複数話かつログイン済みユーザーだった場合、購入済みタブを表示すること */
  const isPurchased = totalPurchasedLength > 0 && seasonType === G_VideoSeasonType.MultiEpisodes && isLoggedIn;
  const existBonus = totalSpecialLength > 0;
  const existPreview = isAdultBlock && totalPreviewLength > 0;

  const getTabLabelText = (tab: TabType) => {
    switch (tab) {
      case "episode":
        return isEpisode ? `エピソード(${totalEpisodeLength})` : "";
      case "purchased":
        return isPurchased ? `購入済み(${totalPurchasedLength})` : "";
      case "special":
        return existBonus ? `特典映像(${totalSpecialLength})` : "";
      case "productInfo":
        return "作品情報";
      case "preview":
        return existPreview ? `予告編(${totalPreviewLength})` : "";
      case "review":
        return "レビュー";
      default:
        throw new Error(`${tab} is not TabType.`);
    }
  };

  const tabOptions = Object.values(MULTI_SELECT_TABS).flatMap((tab) => {
    const tabLabel = getTabLabelText(tab);
    return tabLabel ? [{ tabLabel, value: tab }] : [];
  }, []);

  return (
    <Tab
      name="TabBlocks"
      selectValue={selectedTabValue}
      options={tabOptions}
      onChangeTab={(targetValue) => onChangeTabValue(targetValue as TabType)}
    />
  );
});

VideoSeasonTabTitle.displayName = "VideoSeasonTabTitle";
